/**
 * @fileoverview The public gift pages' language: a ?lang=es (or en) in the address opens
 * the page in that language, as the gift and receipt emails' links say;
 * otherwise the app's own (the device's choice, or the browser's). The
 * pages' switch (chooseGiftLanguage) redraws every gift page at once: the
 * app's translations are not reactive by themselves (useTranslation).
 *
 * Where: every gift page and GiftShell.
 * API: none.
 * Settings: ?lang=en|es; the device's start language (shared/lib/startLanguage).
 * Watch out: the module-level `redraws` set is how one switch redraws every
 * gift component on the page at once.
 *
 * @module useGiftLanguage
 */

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { chooseStartLanguage } from '@/shared/lib/startLanguage';

/** The gift pages drawn now, each redrawn when the language is switched. */
const redraws = new Set<() => void>();

/**
 * Switches the gift pages to a language, kept on this device as the sign-in
 * page's switch keeps it, and redraws them in it.
 *
 * @param language - English or Spanish.
 */
export function chooseGiftLanguage(language: 'en' | 'es'): void {
  chooseStartLanguage(language);
  redraws.forEach((redraw) => redraw());
}

/**
 * The page's language, from the address when it says one, put on the page
 * before the first words are drawn; the component is redrawn when the
 * language is switched (chooseGiftLanguage).
 *
 * @returns The language the page is in.
 */
export function useGiftLanguage(): 'en' | 'es' {
  const [params] = useSearchParams();
  const { i18n } = useTranslation();
  const [, redraw] = React.useReducer((n: number) => n + 1, 0);
  // Once, on the first draw: the address's language, as the stop page does.
  React.useState(() => {
    const wanted = params.get('lang');
    if ((wanted === 'en' || wanted === 'es') && !i18n.language.startsWith(wanted)) i18n.changeLanguage(wanted);
    return wanted;
  });
  React.useEffect(() => {
    redraws.add(redraw);
    return () => { redraws.delete(redraw); };
  }, []);
  return i18n.language.startsWith('es') ? 'es' : 'en';
}
